import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Accordion, AccordionItem, Image } from '@nextui-org/react';
import { Icon } from '@iconify/react';
import { AttachmentMediaWithFallback, generateCircuitryLogoSvgBlobUrl } from '@circuitry-ai/doc-ui';
import { scrollChatToBottom } from './chatAutoScroll';
import type { WorkflowStep } from './WorkflowProgressAccordion';

/** Normalizes a raw base64 frame or an existing data URL into an `<img>`-ready source. */
const toImageSrc = (frame: string): string =>
  frame.startsWith('data:') ? frame : `data:image/png;base64,${frame}`;

interface AdvisorThinkingProcessProps {
  readonly processes: WorkflowStep[];
  readonly isComplete: boolean;
  readonly isStreaming: boolean;
  /** Advisor knowledge steps vs agent workflow executing/completed pairs. */
  readonly variant?: 'advisor' | 'agent';
}

const THINKING_CYCLE_LABELS = ['Thinking…', 'Analyzing your request…', 'Working on your response…'];
const AGENT_CYCLE_LABELS = ['Running workflow…', 'Executing steps…', 'Working on your request…'];
const ACCORDION_KEY = 'thinking-process';

/** Host apps (e.g. PAL) may not import doc-web global styles; keep animations on the component. */
const ADVISOR_THINKING_STYLES = `
@keyframes advisorThinkSpin {
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
}
@keyframes advisorThinkStepReveal {
  from { opacity: 0; transform: translateX(-5px); }
  to { opacity: 1; transform: translateX(0); }
}
`;

function formatAgentDuration(seconds: number): string {
  if (!Number.isFinite(seconds) || seconds <= 0) return 'Completed';
  if (seconds >= 60) {
    const minutes = Math.floor(seconds / 60);
    const rem = Math.floor(seconds % 60);
    return `Completed in ${minutes}:${rem.toString().padStart(2, '0')} m`;
  }
  return `Completed in ${Math.round(seconds)}s`;
}

function formatStepDuration(seconds: number | undefined): string {
  if (seconds == null || !Number.isFinite(seconds) || seconds <= 0) return '0.00s';
  if (seconds >= 60) {
    const minutes = Math.floor(seconds / 60);
    const rem = Math.floor(seconds % 60);
    return `${minutes}:${rem.toString().padStart(2, '0')} m`;
  }
  return `${seconds.toFixed(2)}s`;
}

/** Collapse executing/completed pairs into one row per workflow node. */
function normalizeAgentPhases(phases: WorkflowStep[]): WorkflowStep[] {
  const steps: WorkflowStep[] = [];

  for (let i = 0; i < phases.length; i += 1) {
    const phase = phases[i];
    const msg = phase.message.trim();
    const phaseType = phase.type.trim().toLowerCase();
    if (!msg) continue;

    if (phaseType === 'executing') {
      const next = phases[i + 1];
      const nextType = next?.type.trim().toLowerCase();
      if (next && next.message.trim() === msg && nextType === 'completed') {
        steps.push({
          type: msg,
          message:
            next.duration_sec != null && next.duration_sec > 0
              ? formatAgentDuration(next.duration_sec)
              : 'Completed',
          duration_sec: next.duration_sec,
          skipped: next.skipped ?? phase.skipped,
          screenshot: next.screenshot ?? phase.screenshot,
        });
        i += 1;
      } else {
        steps.push({ type: msg, message: 'Running…', skipped: phase.skipped, screenshot: phase.screenshot });
      }
    } else if (phaseType === 'completed') {
      steps.push({
        type: msg,
        message:
          phase.duration_sec != null && phase.duration_sec > 0
            ? formatAgentDuration(phase.duration_sec)
            : 'Completed',
        duration_sec: phase.duration_sec,
        skipped: phase.skipped,
        screenshot: phase.screenshot,
      });
    } else {
      steps.push({
        type: phase.type.trim(),
        message: msg,
        duration_sec: phase.duration_sec,
        skipped: phase.skipped,
        screenshot: phase.screenshot,
      });
    }
  }

  return steps;
}

function stepIcon(
  type: string,
  index: number,
  variant: 'advisor' | 'agent',
  isActive?: boolean,
  isStepComplete?: boolean
): string {
  if (variant === 'agent') {
    if (isActive) return 'lucide:clock';
    if (isStepComplete) return 'lucide:check';
    return index === 0 ? 'lucide:play' : 'lucide:settings';
  }

  const normalized = type.toLowerCase();
  if (index === 0 || normalized.includes('understand') || normalized.includes('analyz')) {
    return 'lucide:clock';
  }
  if (normalized.includes('search') || normalized.includes('knowledge')) {
    return 'lucide:search';
  }
  if (
    normalized.includes('read') ||
    normalized.includes('doc') ||
    normalized.includes('manual') ||
    normalized.includes('guideline')
  ) {
    return 'lucide:file-text';
  }
  if (normalized.includes('compos') || normalized.includes('response')) {
    return 'lucide:clock';
  }
  return 'lucide:file-text';
}

function formatStepLabel(type: string): string {
  return type.trim().replace(/_/g, ' ');
}

function ThinkingDots() {
  return (
    <span className="inline-flex items-center gap-0.5 ml-1">
      <span className="h-1 w-1 animate-bounce rounded-full bg-default-400 [animation-delay:-0.2s]" />
      <span className="h-1 w-1 animate-bounce rounded-full bg-default-400 [animation-delay:-0.1s]" />
      <span className="h-1 w-1 animate-bounce rounded-full bg-default-400" />
    </span>
  );
}

interface ThinkingStepRowProps {
  readonly step: WorkflowStep;
  readonly index: number;
  readonly total: number;
  readonly isActive: boolean;
  readonly isComplete: boolean;
  readonly variant: 'advisor' | 'agent';
}

function ThinkingStepRow({ step, index, total, isActive, isComplete, allComplete, variant }: ThinkingStepRowProps & { allComplete?: boolean }) {
  const [expanded, setExpanded] = useState(false);
  const isFirst = index === 0;
  const isLast = index === total - 1;
  const isMiddleComplete = isComplete && !isFirst && !isLast;
  const showDetailByDefault = allComplete ? isFirst || isLast : isFirst;
  const showDetail =
    isActive ||
    (isComplete && showDetailByDefault) ||
    (isMiddleComplete && expanded);
  const durationLabel =
    isComplete && !isActive ? formatStepDuration(step.duration_sec) : null;

  return (
    <div
      className={`flex items-stretch gap-0 relative animate-[advisorThinkStepReveal_0.22s_ease-out_both] ${
        isActive ? 'think-step-active' : ''
      } ${isComplete ? 'think-step-complete' : ''} ${isFirst ? 'think-step-first' : ''} ${
        isMiddleComplete && expanded ? 'think-step-expanded' : ''
      }`}
    >
      <div className="flex flex-col items-center w-[22px] shrink-0 pt-0.5">
        <div
          className={`w-[18px] h-[18px] flex items-center justify-center shrink-0 ${
            isActive ? 'text-primary' : step.skipped ? 'text-default-500' : 'text-default-400'
          }`}
          title={step.skipped ? 'Skipped' : undefined}
        >
          <Icon
            icon={
              step.skipped && !isActive
                ? 'mdi:debug-step-over'
                : stepIcon(step.type, index, variant, isActive, isComplete)
            }
            width={14}
            height={14}
          />
        </div>
        {!isLast ? <div className="w-px flex-1 min-h-[14px] bg-default-200 mt-0.5" /> : null}
      </div>
      <div
        className={`flex-1 min-w-0 pb-3 pl-2 ${
          isActive
            ? 'border-[1.5px] border-primary rounded-lg px-2.5 py-1.5 mb-2.5 bg-content1'
            : isMiddleComplete
              ? 'cursor-pointer rounded-md hover:bg-default-100 transition-colors'
              : ''
        }`}
        {...(isMiddleComplete
          ? {
              role: 'button',
              tabIndex: 0,
              onClick: () => setExpanded((prev) => !prev),
              onKeyDown: (event: React.KeyboardEvent) => {
                if (event.key === 'Enter' || event.key === ' ') {
                  event.preventDefault();
                  setExpanded((prev) => !prev);
                }
              },
            }
          : {})}
      >
        <div className="grid grid-cols-[minmax(0,1fr)_3.25rem_14px] items-center gap-x-2 w-full min-w-0">
          <span
            className={`min-w-0 truncate text-[13px] leading-6 ${
              isActive ? 'text-primary font-medium' : 'text-default-500'
            }`}
          >
            {formatStepLabel(step.type)}
          </span>
          {isComplete && !isActive ? (
            <span className="text-right text-[11px] leading-6 tabular-nums text-default-400">
              {durationLabel}
            </span>
          ) : (
            <span aria-hidden />
          )}
          {isMiddleComplete ? (
            <Icon
              icon="lucide:chevron-right"
              width={14}
              height={14}
              className={`shrink-0 text-default-400 transition-transform ${expanded ? 'rotate-90' : ''}`}
            />
          ) : null}
        </div>
        {showDetail ? (
          <>
            {step.message.trim() ? (
              <div
                className={`text-xs leading-relaxed text-default-500 mt-1 px-2.5 py-1.5 rounded-md border border-default-200 bg-default-100 ${
                  isComplete && isFirst && !isActive ? 'opacity-75' : ''
                } ${isActive ? 'bg-primary/5 border-primary/20' : ''}`}
              >
                {step.message.trim()}
              </div>
            ) : null}
            {step.screenshot ? (
              <div className="mt-1 max-w-[220px]">
                <AttachmentMediaWithFallback
                  url={toImageSrc(step.screenshot)}
                  alt={`${formatStepLabel(step.type)} preview`}
                  isVideo={false}
                  isAudio={false}
                />
              </div>
            ) : null}
          </>
        ) : null}
      </div>
    </div>
  );
}

function ThinkingSpinnerIndicator({
  cycleLabel,
  elapsedSeconds,
  progressPct,
  showProgress,
  logoSrc,
}: Readonly<{
  cycleLabel: string;
  elapsedSeconds: number;
  progressPct: number;
  showProgress: boolean;
  logoSrc: string;
}>) {
  return (
    <>
      <div className="flex items-center gap-2 py-2.5 pt-2.5 pb-0.5 ml-0.5">
        <span className="relative inline-flex items-center justify-center w-9 h-9 shrink-0">
          <span className="absolute inset-0 rounded-full border-[2.5px] border-transparent border-t-secondary border-r-secondary animate-[advisorThinkSpin_1s_linear_infinite]" />
          <Image
            alt=""
            src={logoSrc}
            width={20}
            height={20}
            className="relative z-[1] object-contain"
            removeWrapper
          />
        </span>
        <span className="text-[12.5px] text-default-400">{cycleLabel}</span>
        <span className="text-[12.5px] text-default-400 mx-1">·</span>
        <span className="text-[12.5px] text-default-400">{elapsedSeconds}s</span>
      </div>
      {showProgress ? (
        <div className="h-[3px] bg-default-200 rounded-full mx-0.5 mt-1.5 mb-0.5 overflow-hidden">
          <div
            className="h-full bg-primary rounded-full transition-[width] duration-500 ease-out"
            style={{ width: `${progressPct}%` }}
          />
        </div>
      ) : null}
    </>
  );
}

export function AdvisorThinkingProcess({
  processes,
  isComplete,
  isStreaming,
  variant = 'advisor',
}: AdvisorThinkingProcessProps) {
  const rawProcesses = useMemo(
    () =>
      processes.filter(
        (step) =>
          typeof step.type === 'string' &&
          typeof step.message === 'string' &&
          step.type.trim().length > 0 &&
          step.message.trim().length > 0
      ),
    [processes]
  );

  const validProcesses = useMemo(
    () => (variant === 'agent' ? normalizeAgentPhases(rawProcesses) : rawProcesses),
    [rawProcesses, variant]
  );

  const cycleLabels = variant === 'agent' ? AGENT_CYCLE_LABELS : THINKING_CYCLE_LABELS;
  const containerClass =
    variant === 'agent' ? 'self-start w-full max-w-[900px]' : 'self-start w-full max-w-[80%]';
  const completedLabel = (count: number) =>
    variant === 'agent' ? `Completed ${count} steps` : `Searched ${count} steps`;

  const logoSrc = useMemo(() => generateCircuitryLogoSvgBlobUrl('#D4007E'), []);
  // An already-finished turn mounts in its settled state: collapsed, no timers,
  // no collapse animation. Otherwise switching conversations replays the reveal.
  const [accordionKeys, setAccordionKeys] = useState<Set<string>>(
    () => (isComplete ? new Set<string>() : new Set([ACCORDION_KEY]))
  );
  const [showIndicator, setShowIndicator] = useState(!isComplete);
  const [showProgress, setShowProgress] = useState(false);
  const [elapsedSeconds, setElapsedSeconds] = useState(0);
  const [cycleLabel, setCycleLabel] = useState(cycleLabels[0]);
  const [progressPct, setProgressPct] = useState(isComplete ? 100 : 0);
  const hasAutoCollapsedRef = useRef(isComplete);
  const scrollRef = useRef<HTMLDivElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);
  const streamStartRef = useRef<number | null>(null);

  const isExpanded = accordionKeys.has(ACCORDION_KEY);

  const activeIndex = useMemo(() => {
    if (isComplete || validProcesses.length === 0) return -1;
    // While streaming, keep the latest step open so its message (and screenshot) stay visible
    // inside the accordion — web-agent phases are not executing/completed pairs.
    return validProcesses.length - 1;
  }, [isComplete, validProcesses]);

  const headerLabel =
    !isComplete && validProcesses.length > 0
      ? variant === 'agent'
        ? validProcesses[activeIndex]?.type ?? 'Running workflow…'
        : formatStepLabel(validProcesses[activeIndex]?.type ?? 'Thinking')
      : variant === 'agent'
        ? 'Running workflow…'
        : 'Thinking';

  useEffect(() => {
    setCycleLabel(cycleLabels[0]);
  }, [variant, cycleLabels]);

  useEffect(() => {
    if (isComplete) return undefined;

    if (streamStartRef.current == null) {
      streamStartRef.current = Date.now();
    }

    const progressTimer = window.setTimeout(() => setShowProgress(true), 5000);
    let tick = 0;

    const intervalId = window.setInterval(() => {
      tick += 1;
      setElapsedSeconds(tick);
      if (tick % 3 === 0) {
        setCycleLabel(cycleLabels[(tick / 3) % cycleLabels.length]);
      }

      const startedAt = streamStartRef.current ?? Date.now();
      const durationEstimateMs = validProcesses.reduce(
        (sum, step) => sum + (Number(step.duration_sec) || 0) * 1000,
        0
      );
      const estimatedTotalMs = Math.max(
        durationEstimateMs,
        validProcesses.length * 2000,
        4000
      );
      const pct = Math.min(95, Math.round(((Date.now() - startedAt) / estimatedTotalMs) * 100));
      setProgressPct(pct);
    }, 1000);

    return () => {
      window.clearTimeout(progressTimer);
      window.clearInterval(intervalId);
    };
  }, [isComplete, validProcesses.length, cycleLabels]);

  useEffect(() => {
    if (!isComplete || hasAutoCollapsedRef.current) return;

    hasAutoCollapsedRef.current = true;
    setProgressPct(100);
    window.setTimeout(() => {
      setShowIndicator(false);
      setShowProgress(false);
      setAccordionKeys(new Set());
    }, 400);
  }, [isComplete]);

  useEffect(() => {
    if (!scrollRef.current) return;
    scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [validProcesses.length, isStreaming, showIndicator, isExpanded]);

  useEffect(() => {
    if (isComplete) return undefined;

    const contentEl = contentRef.current;
    if (!contentEl) return undefined;

    const pinChatToBottom = () => {
      scrollChatToBottom('auto');
    };

    pinChatToBottom();
    const observer = new ResizeObserver(pinChatToBottom);
    observer.observe(contentEl);
    return () => observer.disconnect();
  }, [isComplete, validProcesses.length, showIndicator, showProgress, isExpanded]);

  if (validProcesses.length === 0) {
    if (!isStreaming) return null;
  }

  if (isComplete && validProcesses.length === 0) return null;

  const accordionTitle = isComplete ? (
    <span className="inline-flex items-center gap-1.5 text-default-500 text-sm font-medium">
      <Icon
        icon="lucide:chevron-right"
        width={14}
        height={14}
        className={`text-default-400 transition-transform ${isExpanded ? 'rotate-90' : ''}`}
      />
      <span>{completedLabel(validProcesses.length)}</span>
    </span>
  ) : (
    <span className="inline-flex items-center gap-1.5 text-default-500 text-sm font-medium min-w-0">
      <Icon icon="lucide:clock" width={14} height={14} className="text-default-400 shrink-0" />
      <span className="truncate">{headerLabel}</span>
      {!isExpanded ? <ThinkingDots /> : null}
      <Icon
        icon="lucide:chevron-right"
        width={14}
        height={14}
        className={`text-default-400 shrink-0 transition-transform ml-auto ${isExpanded ? 'rotate-90' : ''}`}
      />
    </span>
  );

  return (
    <div className={containerClass}>
      <style>{ADVISOR_THINKING_STYLES}</style>
      <Accordion
        className="!px-0 w-full"
        selectedKeys={accordionKeys}
        onSelectionChange={(keys) => setAccordionKeys(keys as Set<string>)}
        selectionMode="multiple"
      >
        <AccordionItem
          key={ACCORDION_KEY}
          aria-label={isComplete ? completedLabel(validProcesses.length) : headerLabel}
          title={accordionTitle}
          indicator={<></>}
          classNames={{
            content: 'pl-0 pr-0 pt-0',
            base: 'w-full !border-none !shadow-none !bg-transparent !px-0',
            title: 'text-sm w-full',
            trigger: 'py-0 pb-2',
          }}
        >
          <div ref={contentRef} className="flex flex-col gap-0">
            {validProcesses.length > 0 ? (
              <div ref={scrollRef} className="flex flex-col max-h-[280px] overflow-y-auto">
                {validProcesses.map((step, index) => (
                  <ThinkingStepRow
                    key={`${step.type}-${index}`}
                    step={step}
                    index={index}
                    total={validProcesses.length}
                    isActive={!isComplete && index === activeIndex}
                    isComplete={isComplete || activeIndex === -1 || index < activeIndex}
                    allComplete={isComplete}
                    variant={variant}
                  />
                ))}
              </div>
            ) : null}

            {!isComplete && showIndicator ? (
              <ThinkingSpinnerIndicator
                cycleLabel={cycleLabel}
                elapsedSeconds={elapsedSeconds}
                progressPct={progressPct}
                showProgress={showProgress}
                logoSrc={logoSrc}
              />
            ) : null}
          </div>
        </AccordionItem>
      </Accordion>
    </div>
  );
}
